import PKCE from 'js-pkce';
import jwtDecode from 'jwt-decode';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import config, { getBaseUrl, getApiUrl, handleClientDomainRedirection } from './config';
import { UserInfo } from './model/UserInfo';
import { postApi, putApi } from './useFetch';
import { useCookies } from 'react-cookie';
import Permissions from '../constants/Permissions';
import AppBusinessKey from '../constants/app-mapping';
import { API_TOKEN_KEY, REFRESH_TOKEN_EXPIRY, ACCESS_TOKEN_EXPIRY, TIME_STAMP, IS_LOGGED_IN, PERMISSIONSLIST, REFRESH_TOKEN_KEY, ID_TOKEN } from '../constants/localStorageConstants';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

/**
 * Context for User Information
 */
const UserContext = React.createContext(null);

/**
 * Props definition for the User Provider
 */
export interface UserProviderProps {
  children: React.ReactNode;
}
type RoleFilterResult = {
  validRoles: {
    name: string;
    permission: string[];
    memberId: string;
  }[];
  validRoleType: string;
};

/**
 * The provider for the UserContext
 * @param props
 * @returns JSX for the UserContext.Provider
 */
export const UserProvider: FC<UserProviderProps> = (props: UserProviderProps) => {
  const TRUE = 'true';

  const fromCache = getUserInfo();
  const [userInfo, setUserInfo] = useState(fromCache);

  const [cookies, setCookie, removeCookie] = useCookies(['access_token', 'member_id', 'member_name']);
  const [permissionsList, setPermissionsList] = useState<any>();
  const [searchParams] = useSearchParams();
  const [showErrorMsg, setShowErrorMsg] = useState<boolean>(false);
  const [showInfo, setShowInfo] = useState(false);
  const {
    data: guardianData,
    isFetching: isGuardianFetching,
    refetch: refetchGuardianAuth,
  } = useQuery({
    queryKey: ['guardianAuthorization'],
    queryFn: guardianAuthorization,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  //
  // On route changes, need to do some work to make sure the session is still valid.
  const location = useLocation();
  const navigate = useNavigate();

  const handleClearLocalStorage = () => {
    localStorage.removeItem(ACCESS_TOKEN_EXPIRY);
    localStorage.removeItem(REFRESH_TOKEN_EXPIRY);
    localStorage.removeItem(ID_TOKEN);
    localStorage.removeItem(API_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(IS_LOGGED_IN);
    localStorage.removeItem(TIME_STAMP);
    localStorage.removeItem(PERMISSIONSLIST);
    // Also clear HPG status
    localStorage.removeItem('isHPGClient');
  }

  function clearSessionAndRedirectToLogin(fssocode: string) {
    handleClearLocalStorage();
    removeCookie('access_token');
    removeCookie('member_id');
    let pkce_client = getPkceClient();
    let URL = pkce_client.authorizeUrl({ kc_idp_hint: fssocode });
    window.location.replace(URL);
  }

  const refreshTokenExpiry = localStorage.getItem(REFRESH_TOKEN_EXPIRY);
  const isProduction = process.env.REACT_APP_ENV === 'production';
  const dashboardUrl = isProduction ? '/dashboard' : `/${process.env.REACT_APP_ENV}/dashboard`;

  const getValidRolesFromAccounts = (res: any): RoleFilterResult => {
    if (!res?.userAccounts?.length) {
      return {
        validRoles: [],
        validRoleType: '',
      };
    }

    const appEnv = process.env.REACT_APP_ENV?.toLowerCase();

    const byHealthSystem: Record<string, { name: string; memberId: string; permission: string[] }> = {};
    let validRoleType = '';
    let accounts: any = {};
    res.userAccounts.forEach((account: any) => {
      const healthSystems = account?.organization?.healthSystems;
      if (!Array.isArray(healthSystems) || !healthSystems.length) {
        return;
      }

      if (healthSystems[0].name === 'All') {
        return;
      }
      accounts=account;
    });

      const roleType = accounts?.additionalInfo?.roleType || '';
      
      accounts?.organization?.healthSystems?.forEach((org: any) => {
        const normalizedName = String(org.name).trim();
        const orgKey = normalizedName.toLowerCase();
        if (!byHealthSystem[orgKey]) {
          byHealthSystem[orgKey] = {
            name: normalizedName,
            memberId: org.id,
            permission: [],
          };
        }

        (org?.permissions || []).forEach((permissionValue: any) => {
          const permission = permissionValue.split(':');
          const appKey = permission[0];
          const moduleName = permission[1];
          const featurePermission = permission[2];
          if (!appKey || !moduleName || !featurePermission || !featurePermission.includes('.')) {
            return;
          }

          const appName = appEnv ? AppBusinessKey?.[appEnv]?.[appKey] : undefined;
          if (!appName) return;
      
          //uncomment if you want settings/sporlight
          //if (!process.env.REACT_APP_BUSINESS_KEY.split(',')?.includes(appKey)) return;

          if (!byHealthSystem[orgKey].permission.includes(permissionValue)) {
            byHealthSystem[orgKey].permission.push(permissionValue);
          }
          if (!validRoleType && roleType) {
            validRoleType = roleType;
          }
        });
      });
  
    return {
      validRoles: Object.values(byHealthSystem).filter(
        (item) => Array.isArray(item.permission) && item.permission.length > 0,
      ),
      validRoleType,
    };
  };

  const parseGuardianResponse = (res: any) => {
    let userAccount;
    if (res && res?.userAccounts?.length) {
      // Guardian user can have multiple accounts like Tenant Admin, Tenant User;
      // Below filter helps to get account with member details.
      userAccount = res.userAccounts.filter((item: any) => item.organization.healthSystems.length);
    } else {
      // return empty if no member associations found for the user in guardian
      return [];
    }
    let orgList: any = [];
    let isInternalUser: boolean = false;
    let isAdminUser: boolean = false;
    let isSelfServiceUser: boolean = false;

    res?.userAccounts.forEach((account: any) => {
      if (account.organization.healthSystems[0].name !== 'All') {
        orgList = account.organization.healthSystems;
        isInternalUser = account.isInternalUser;
        // Check the user type
        if (
          account.additionalInfo.roleType.toLowerCase().includes('admin') ||
          account.additionalInfo.roleType.toLowerCase().includes('super')
        ) {
          isAdminUser = true;
        } else if (account.additionalInfo.roleType.toLowerCase().includes('self-service')) {
          isSelfServiceUser = true;
        }
      }
    });
    const list: any = {};

    orgList.forEach((org: any) => {
      if (!list[org.id]) {
        list[org.id] = {
          name: org.name,
          subscriptions: {},
          isInternalUser: isInternalUser,
          isAdminUser: isAdminUser,
          isSelfServiceUser: isSelfServiceUser,
        };
      }
      org.permissions.forEach((x: any) => {
        const permission = x.split(':');
        const appKey = permission[0];

        // Consider only app match the current environment
        const appEnv = process.env.REACT_APP_ENV.toLowerCase();
        const appName = AppBusinessKey[appEnv][appKey];
        if (!appName) return;

        if (!list[org.id]['subscriptions'][appKey]) {
          list[org.id]['subscriptions'][appKey] = {};
        }

        if (process.env.REACT_APP_BUSINESS_KEY.split(',')?.includes(appKey)) {
          const features = permission[2].split('.');
          const _features = features[0].trim();
          if (!list[org.id]['subscriptions'][appKey][_features]) {
            list[org.id]['subscriptions'][appKey][_features] = [];
          }
          list[org.id]['subscriptions'][appKey][_features].push(features[1]);
        }
      });
    });
    return list;
  };

  const handleFetchFailure = (res: any): boolean => {
    if (res.status === 'failure') {
      setShowErrorMsg(true);
      return true;
    }
    return false;
  };

  const handleInfoDisplay = (formattedJSON: any) => {
    // Show info notification if no associations found, otherwise hide it
    if (Object.keys(formattedJSON).length) {
      setShowInfo(false);
    } else {
      setShowInfo(true);
    }
  };


  const handleCookies = (formattedJSON: any) => {

    // Temporary code to set permissions for testing purpose
    // TODO: Remove this code after testing - Guardian client creation
    if (process.env.REACT_APP_ENV === 'dev') {
      formattedJSON['deveng1']['subscriptions']['LNK-SURGICAL-1111'] = {};
    }
  };

  const getGuardianAuth = () => {
    processGuardianAuth();
  };

  useEffect(() => {
    const unprotectedPaths = ['/', '/logout', '/auth'];
    const isProtectedRoute = !unprotectedPaths.includes(location.pathname);

    const fssocode = searchParams.get('fssocode');
    if (window.location.pathname === dashboardUrl && fssocode) {
      // Get dynamic base URL
      const baseURL = getBaseUrl();
      window.location.replace(`${baseURL}/?fssocode=${fssocode}`);
      return;
    }

    if (!refreshTokenExpiry || refreshTokenExpiry === 'undefined') {
      if (isProtectedRoute) {
        handleClearLocalStorage();
        navigate('/');
      }
    } else {
      // Only fetch permissions on protected routes — '/' and '/auth' manage their own auth flow
      if (isProtectedRoute && !getPermissionListOrFromStorage()) {
        getGuardianAuth();
      }
      // Determine minutes to refresh token expiration
      let now = new Date().getTime();
      let sessionEndTime = new Date(refreshTokenExpiry);
      let remainingMinutes = (sessionEndTime.getTime() - now) / 60000;

      // If the token is expired, clear storage and redirect to home
      if (remainingMinutes < 0) {
        handleClearLocalStorage();
        setUserInfo(undefined);
        if (isProtectedRoute) {
          logout();
        }
      }
    }
  }, [location.pathname]);

  async function isTokenValid() {
    try {
      const response = await postApi('/validateToken', {
        method: 'POST',
      });

      if (response.status === 200) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  }

  /**
   * Set the context's UserInfo
   * @param info User information
   */
  function updateUserState(info: UserInfo) {
    setUserInfo(info);
  }

  /**
   * Return User Info from JWT token
   * @returns UserInfo
   */
  function getUserInfo(): UserInfo {
    const token: string = getApiToken();
    const info = userInfoFromToken(token);
    return info;
  }

  function userInfoFromToken(token: string): UserInfo {
    if (!token || token === 'undefined') {
      return;
    }
    try {
      const decoded: any = jwtDecode(token);

      // Unique_name is for AAD, where OHID uses email
      return {
        id: undefined,
        isActive: true,
        authType: decoded.identity_provider === 'uhg-ms-id' ? 'AAD' : 'OHID',
        firstName: decoded.given_name,
        lastName: decoded.family_name,
        fullName: decoded.name,
        email: decoded.unique_name || decoded.email,
        role: decoded.role,
        issuedAt: decoded.iat,
        expiresAt: decoded.exp,
      };
    } catch {
      return;
    }
  }

  /**
   * Set a timestamp for the most current user interaction with the page.
   * @returns number
   */
  function setLastUserInteraction(): number {
    let lastTimeStamp = Date.now();
    localStorage.setItem(TIME_STAMP, lastTimeStamp.toString());
    return lastTimeStamp;
  }

  /**
   * Timestamp for the most current user interaction with the page.
   * @returns number
   */
  function getLastUserInteraction(): number {
    let lastTimeStamp = localStorage.getItem(TIME_STAMP);
    if (!lastTimeStamp) {
      return 0;
    }
    return new Date(parseInt(lastTimeStamp)).getTime();
  }

  /**
   * Clear out the most recent interaction timestamp.
   */
  function clearLastUserInteraction() {
    localStorage.removeItem(TIME_STAMP);
  }

  /**
   * Return true if the user is logged in.
   * @returns boolean
   */
  function isLoggedIn(): boolean {
    return localStorage.getItem(IS_LOGGED_IN) === TRUE;
  }

  /**
   * Based on the authentication method, setup PKCE and redirect to authenticator.
   * Timeouts around navigation is to allow time for localStorage writes to flush.
   */
  async function login(fssocode: string) {
    // Persist fssocode for login redirection
    localStorage.setItem('fssocode', fssocode);
    // Check refresh token expiry first
    if (!refreshTokenExpiry || refreshTokenExpiry === 'undefined') {
      clearSessionAndRedirectToLogin(fssocode);
      return;
    }

    let now = new Date().getTime();
    let sessionEndTime = new Date(refreshTokenExpiry);
    let remainingMinutes = (sessionEndTime.getTime() - now) / 60000;
    if (remainingMinutes < 0) {
      clearSessionAndRedirectToLogin(fssocode);
      return;
    }

    if (await isTokenValid()) {
      // Get dynamic base URL
      const baseURL = getBaseUrl();
      window.location.replace(`${baseURL}/auth`);
    } else {
      // Be sure local state is clear before logging in
      clearSessionAndRedirectToLogin(fssocode);
    }
  }

  /**
   * After getting a valid auth code from the authentication service, exchange it
   * for an API access token.
   * @param url
   * @returns Promise
   */
  function exchangeForAccessToken(url: string) {
    const pkce_client = getPkceClient();

    // Now just get the JWT token from the authentication server
    return pkce_client
      .exchangeForAccessToken(url)
      .then(async (resp) => {
        await forwardTokenResponse(resp);
        postApi('login-success', {}).then((res: any) => {
          if (res.status === 200) {
            console.info('NESS Log to backend - Login');
          }
        });
      })
      .catch((e) => {
        console.error('Error exchanging access token: ');
        console.error(e);
      });
  }

  /**
   * Clear local and session storage, then redirect to the login screen.
   */
  async function logout() {
    const idToken = localStorage.getItem(ID_TOKEN);
    removeCookie('access_token');
    removeCookie('member_id');
    await postApi('logout', {}).then((res: any) => {
      if (res.status === 200) {
        console.info('NESS Log to backend - Logout');
      }
    });
    handleClearLocalStorage();
    // If idToken is present, log out of the OIDC provider
    if (idToken != null) {
      const baseURL = getBaseUrl();
      const postLogoutURL = encodeURI(baseURL);
      const url = `${process.env.REACT_APP_OIDC_BASE_URL}/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${postLogoutURL}`;
      window.location.replace(url);
    } else {
      // Get dynamic base URL
      const baseURL = getBaseUrl();
      // If not present assume logout is already done, redirect to home which will redirect to login page
      window.location.replace(baseURL);
    }
  }

  /**
   * Obtain a new access token from the server using the refresh token.
   */
  const refreshToken = async()  => {
    putApi(
      'token',
      {},
      {
        'Content-Type': 'application/json',
        'Api-version': '4',
      },
    )
      .then((tokenResponse) => {
        if (tokenResponse.status !== 200) {
          console.error('Token response was not successful');
        } else {
          tokenResponse.json().then((body) => {
            saveExpiryTime(body);
          });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Save the response data obtained from the authentication server.
   * @param resp The body from a token request
   */
  function saveTokenResponse(resp: any) {
    let accessToken = resp.access_token;
    let refreshToken = resp.refresh_token;
    localStorage.setItem(ID_TOKEN, resp.id_token); // REQUIRED FOR LOGOUT
    localStorage.setItem(API_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(IS_LOGGED_IN, TRUE);
  }

  function saveExpiryTime(resp: any) {
    if(resp.id_token) {
      localStorage.setItem(ID_TOKEN, resp.id_token);
    }
    localStorage.setItem(ACCESS_TOKEN_EXPIRY, resp.access_token_expiry);
    localStorage.setItem(REFRESH_TOKEN_EXPIRY, resp.refresh_token_expiry);
  }

  async function forwardTokenResponse(resp: any) {
    let accessToken = resp.access_token;
    let refreshToken = resp.refresh_token;
    localStorage.setItem(ID_TOKEN, resp.id_token); // REQUIRED FOR LOGOUT
    //Remove
    localStorage.setItem(API_TOKEN_KEY, accessToken);
    //Remove
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(IS_LOGGED_IN, TRUE);
    await postApi(
      'setToken',
      {
        access_token: accessToken,
        refresh_token: refreshToken
      },
      {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
    )
      .then((tokenResponse) => {
        if (tokenResponse.status !== 200) {
          console.error('Token response was not successful');
        } else {
          tokenResponse.json().then((body) => {
            saveExpiryTime(body);
          });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Return the API token used to authorize calls to the backend
   * @returns string
   */
  function getApiToken(): string {
    return localStorage.getItem(API_TOKEN_KEY);
  }

  /**
   * Factory method to initialize a PKCE client for the provided authentication service
   * @returns PKCE client
   */
  function getPkceClient(): PKCE {
    let pkce_client: PKCE;
    // Get dynamic base URL based on current domain
    const baseURL = getBaseUrl();
    pkce_client = new PKCE({
      client_id: process.env.REACT_APP_OIDC_CLIENT_ID,
      redirect_uri: `${baseURL}/auth`,
      authorization_endpoint: `${process.env.REACT_APP_OIDC_BASE_URL}/auth`,
      token_endpoint: `${process.env.REACT_APP_OIDC_BASE_URL}/token`,
      requested_scopes: 'openid profile email',
      storage: localStorage,
    });
    return pkce_client;
  }

  async function guardianAuthorization() {
    try {
      const apiURL = getApiUrl();
      const req: any = await fetch(`${apiURL}/OneLoginAssociations`, {
        method: 'GET',
        headers: getRequestHeader(),
      });
      if (!req.ok) {
        console.error('Error fetching guardian authorization:', req.statusText);
        return { status: 'failure' };
      }
      const response = await req.json();
      return response;
    } catch (err) {
      console.error(err);
      return { status: 'failure' };
    }
  }

  function getRequestHeader() {
    const token = localStorage.getItem(API_TOKEN_KEY);
    const memberId = cookies['member_id'];
    const isLocalEnv = window.location.hostname === 'localhost';

    const header: any = {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
    };
    if (memberId) {
      header['member_id'] = memberId;
    }
    if (isLocalEnv && token) {
      header['authorization'] = `Bearer ${token}`;
    }
    return header;
  }

  function isInternalUser() {
    const permissions = getPermissionListOrFromStorage();
    if (!permissions) return false;
    let permission = permissions[cookies['member_id']];
    return permission?.isInternalUser;
  }

  const isAllowed = (memberId: string, appIds: string, featureName: string, permission: string) => {
    if (appIds == undefined) {
      return false;
    }
    return appIds
      .split(',')
      .map((i) => {
        return isAllowedSingleAppId(memberId, i, featureName, permission);
      })
      .reduce((x, i) => x || i, false);
  };

  const isAllowedSingleAppId = (memberId: string, appId: string, featureName: string, permission: string) => {
    const permissions = getPermissionListOrFromStorage();
    if (!permissions && !memberId && !appId && featureName) return;
    if (!permission) {
      return Object.keys(permissions)?.includes(featureName);
    } else {
      const _permissions: string[] = permissions[memberId]['subscriptions'][appId]
        ? permissions[memberId]['subscriptions'][appId][featureName]
        : [];
      // Permissions.ALL has highest precedence => return true
      return _permissions?.length && (_permissions?.includes(Permissions.ALL) || _permissions?.includes(permission));
    }
  };

  const isUserAuthorized = (feature: string, permission: string = '', memberId: string = '') => {
    const permissions = getPermissionListOrFromStorage();
    if (!permissions) return;
    if (!memberId) return;
    if (!permission) {
      return Object.keys(permissions)?.includes(feature);
    } else {
      const _permissions: string[] = permissions[memberId][feature] ? permissions[memberId][feature] : [];
      // Permissions.ALL has highest precedence => return true
      return _permissions.length && (_permissions?.includes(Permissions.ALL) || _permissions?.includes(permission));
    }
  };

  function setPermissionListAndPersist(permission: any) {
    setPermissionsList(permission);
    localStorage.setItem(PERMISSIONSLIST, JSON.stringify(permission));
  }

  function getPermissionListOrFromStorage() {
    return permissionsList && Object.keys(permissionsList).length ? permissionsList : JSON.parse(localStorage.getItem(PERMISSIONSLIST));
  }

  function checkPermission(module: string, feature: string, member_id: string, permission: string = '') {
    const appEnv = process.env.REACT_APP_ENV;
    const business_key = Object.keys(AppBusinessKey[appEnv]).find(
      (key) => AppBusinessKey[appEnv][key] === module,
    );
    const subscriptions = getPermissionListOrFromStorage()?.[member_id]?.subscriptions;
    const featureAccess = subscriptions?.[business_key]?.[feature];
    if(!permission) {
      return featureAccess?.length > 0;
    }
    return featureAccess?.includes(permission) || featureAccess?.includes(Permissions.ALL);
  }

  /**
   * Process Guardian authentication response and handle domain redirects
   * @param onNavigate Optional callback function to handle navigation after permission is set
   * @returns void
   */
  const processGuardianAuth = async (onNavigate?: (finalPermissions: any) => void) => {
    try {
      let res: any;
      if (guardianData) {
        // Use cached data — no extra API call within the same session
        res = guardianData;
      } else {
        const result = await refetchGuardianAuth();
        if (result.error) {
          console.error('Error fetching Guardian authorization:', result.error);
          setShowErrorMsg(true);
          return;
        }
        res = result.data;
      }

      // Handle fetch failure
      if (handleFetchFailure(res)) return;

      const formattedJSON = parseGuardianResponse(res);
      handleCookies(formattedJSON);

      // Check domain and client type compatibility before setting permissions
      const domainAction = handleClientDomainRedirection(formattedJSON);

      // Determine the final permissions to set
      const finalPermissions = domainAction.type === 'filter' && domainAction.filteredPermissions
        ? domainAction.filteredPermissions
        : formattedJSON;

      // Set permissions in any case
      setPermissionsList(finalPermissions);

      handleInfoDisplay(finalPermissions);

      // Handle redirects if needed
      if (domainAction.type === 'redirect' && domainAction.url) {
        window.location.replace(domainAction.url);
        return;
      }

      // Call the optional navigation callback if provided
      if (onNavigate) {
        onNavigate(finalPermissions);
      }
    } catch (error: any) {
      console.error('Error fetching Guardian authorization:', error);
      setShowErrorMsg(true);
    }
  };

  return (
    <UserContext.Provider
      value={{
        userInfo,
        permissionsList: getPermissionListOrFromStorage(),
        setPermissionsList: setPermissionListAndPersist,
        updateUser: updateUserState,
        isLoggedIn: isLoggedIn,
        getUserInfo: getUserInfo,
        setLastUserInteraction: setLastUserInteraction,
        getLastUserInteraction: getLastUserInteraction,
        clearLastUserInteraction: clearLastUserInteraction,
        login: login,
        exchangeForAccessToken: exchangeForAccessToken,
        logout: logout,
        refreshToken: refreshToken,
        getApiToken: getApiToken,
        guardianAuthorization: guardianAuthorization,
        isTokenValid: isTokenValid,
        isUserAuthorized: isUserAuthorized,
        isAllowed,
        getRequestHeader,
        isInternalUser,
        userDataConfigEdit: (member_id: string) => checkPermission('Settings', 'User Data Configuration', member_id, 'Edit'),
        userDataConfigRead: (member_id: string) => checkPermission('Settings', 'User Data Configuration', member_id, 'Read'),
        createReportAccess: (member_id: string) =>  checkPermission('Advanced Reporting', 'My Reports', member_id, 'Create'),
        saveExpiryTime,
        forwardTokenResponse,
        generalSettingsEdit: (member_id: string) =>  checkPermission('Settings', 'General Settings', member_id, 'Edit'),
        parseGuardianResponse,
        getValidRolesFromAccounts,
        guardianData,
        handleFetchFailure,
        handleCookies,
        handleInfoDisplay,
        processGuardianAuth,
        isGuardianFetching,
        showErrorMsg,
        setShowErrorMsg,
        showInfo,
        setShowInfo,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export const checkForPermission = (userContext: any, memberId: any, mode?: any) => {
  const appEnv = process.env.REACT_APP_ENV;

  // If memberId is not found in cookie; redirect back to dashboard
  if (!memberId) {
    return false;
  }

  if (mode) {
    let business_key = Object.keys(AppBusinessKey[appEnv]).find(
      (key) => AppBusinessKey[appEnv][key] === 'Advanced Reporting',
    );
    const isAuthorized =
      userContext.isAllowed(memberId, business_key, 'My Reports', Permissions.CREATE) ||
      userContext.isAllowed(memberId, business_key, 'Shared Reports', Permissions.READ);

    if (!isAuthorized) {
      return false;
    }
  } else {
    let business_key_1 = Object.keys(AppBusinessKey[appEnv]).find(
      (key) => AppBusinessKey[appEnv][key] === 'Hospital Quality',
    );
    const isAuthorized = userContext.isAllowed(memberId, business_key_1, 'Reports', Permissions.READ);
    if (!isAuthorized) {
      return false;
    }
  }
  return true;
};

export default UserContext;
